/**
 * 회사 계정 · 신고분포 정보공개 동의 (`/?audit=company`, 63차 2026-09-16)
 *  - 회사 정보·상태(확인 대기/완료), 정보공개 동의 카드(문안 전문·현재 상태·동의/철회·이력), 회사 계정 확인 요청 폼
 *  - 개인 가입자(사업자번호 없음)는 안내만. 색은 파랑·회색만(팔레트 규칙).
 * 설계: docs/superpowers/specs/2026-09-16-disclosure-consent-company-account-design.md §5
 */
import { useCallback, useEffect, useState } from "react";
import { fetchCompanyMe, postConsent, postCompanyRequest, lookupBusiness, fillFromLookup, formatBusinessNo, STATUS_LABEL, ROLE_LABEL } from "./disclosureConsent";

const page = { maxWidth: 860, margin: "0 auto", padding: "24px 20px 60px", color: "var(--audit-text, #1f2937)", fontSize: 13.5, lineHeight: 1.65 };
const h1 = { fontSize: 20, fontWeight: 800, margin: "0 0 4px" };
const muted = { color: "var(--audit-text-muted, #6b7280)" };
const card = { border: "1px solid var(--audit-border, #e5e7eb)", borderRadius: 8, padding: "14px 16px", marginTop: 14, background: "var(--audit-bg, #fff)" };
const h2 = { fontSize: 14.5, fontWeight: 700, margin: "0 0 8px" };
const btn = { border: "1px solid var(--audit-border, #d1d5db)", background: "var(--audit-bg, #fff)", color: "var(--audit-text, #1f2937)", borderRadius: 6, padding: "6px 12px", fontSize: 13, cursor: "pointer", fontFamily: "inherit" };
const primary = { ...btn, background: "#1d4ed8", borderColor: "#1d4ed8", color: "#fff", fontWeight: 700 };
const input = { width: "100%", boxSizing: "border-box", padding: "7px 9px", fontSize: 13, border: "1px solid var(--audit-border, #d1d5db)", borderRadius: 6, fontFamily: "inherit", background: "var(--audit-bg, #fff)", color: "inherit" };
const label = { fontSize: 12, fontWeight: 600, ...muted, marginBottom: 3, display: "block" };

export default function CompanyAccountPage({ fetchImpl, apiBase = "" }) {
  const token = (() => { try { return localStorage.getItem("token"); } catch (_) { return null; } })();
  const opts = { fetchImpl: fetchImpl || fetch, apiBase };
  const [me, setMe] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [showText, setShowText] = useState(false);

  const load = useCallback(() => {
    if (!token) { setErr("로그인이 필요합니다."); return; }
    setErr(null);
    fetchCompanyMe(token, opts).then(setMe).catch((e) => setErr(e.status === 401 ? "세션이 만료되었습니다. 다시 로그인해 주세요." : `불러오기 실패 (${e.message})`));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, apiBase, fetchImpl]);
  useEffect(() => { load(); }, [load]);

  const consent = me?.consent || null;
  const text = me?.consent_text || null;

  const act = async (agreed) => {
    if (!text) return;
    const verb = agreed ? "동의" : "철회";
    if (!window.confirm(`정보공개 ${verb}를 기록합니다. 기록자·시각·문안 버전이 남습니다. 계속할까요?`)) return;
    setBusy(true); setNotice(null);
    try {
      await postConsent(token, agreed, text.version, opts);
      setNotice(`정보공개 ${verb}가 기록되었습니다.`);
      load();
    } catch (e) {
      setNotice(e.status === 409 ? "동의 문안이 바뀌었습니다. 화면을 새로 고쳐 새 문안을 확인한 뒤 다시 눌러 주세요." : `처리 실패 (${e.message})`);
    } finally { setBusy(false); }
  };

  return (
    <div style={page} data-testid="company-account-page">
      <h1 style={h1}>회사 계정 · 정보공개 동의</h1>
      <div style={muted}>회사(사업자번호) 단위로 신고이력 정보공개 동의를 관리하고, 자사 신고 자료를 보기 위한 회사 계정 확인을 요청합니다.</div>
      {err && <div style={{ ...card, color: "#b91c1c" }}>{err}</div>}
      {!err && !me && <div style={{ ...card, ...muted }}>불러오는 중…</div>}

      {me && !me.business_no && (
        <div style={card} data-testid="company-personal">
          <h2 style={h2}>개인 가입자</h2>
          <div>이 계정은 사업자번호 없이 가입한 개인 계정입니다. 신고이력 정보공개 동의와 회사 계정 확인은 <b>기업/사업자</b>로 가입한 계정에서 할 수 있습니다.</div>
          <div style={{ ...muted, marginTop: 6 }}>회사 계정으로 바꾸려면 고객센터(문의/건의)로 사업자등록번호를 알려 주세요. 관리자가 회사 계정을 등록해 드립니다.</div>
        </div>
      )}

      {me && me.business_no && (
        <>
          <div style={card} data-testid="company-profile">
            <h2 style={h2}>회사 정보</h2>
            <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: "4px 10px" }}>
              <span style={muted}>사업자번호</span><span style={{ fontVariantNumeric: "tabular-nums" }}>{formatBusinessNo(me.business_no)}</span>
              <span style={muted}>회사명</span><span>{me.profile?.corpName || <span style={muted}>아직 등록되지 않음</span>}</span>
              <span style={muted}>역할</span><span>{ROLE_LABEL[me.profile?.role] || <span style={muted}>—</span>}</span>
              <span style={muted}>확인 상태</span>
              <span data-testid="company-status">{me.profile ? (STATUS_LABEL[me.profile.status] || me.profile.status) : "확인 요청 전"}{me.profile?.reviewedAt ? <span style={muted}> · {me.profile.reviewedAt}</span> : null}</span>
              {me.profile?.reviewNote && (<><span style={muted}>검토 메모</span><span>{me.profile.reviewNote}</span></>)}
            </div>
            <div style={{ ...muted, marginTop: 8, fontSize: 12 }}>확인이 끝난 회사만 자사 신고 건의 상세(거래처·규격·개별 문서)를 볼 수 있습니다. 다른 회사 자료는 누구에게나 신고비율 수준으로만 보입니다.</div>
          </div>

          <div style={card} data-testid="consent-card">
            <h2 style={h2}>신고이력 정보공개 동의 <span style={{ ...muted, fontWeight: 500 }}>· 신고비율 수준</span></h2>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
              <span data-testid="consent-state" style={{ fontWeight: 700, color: consent?.agreed ? "#1d4ed8" : "var(--audit-text, #1f2937)" }}>
                {consent?.agreed ? "동의함" : consent?.current ? "철회함" : "미동의"}
              </span>
              {consent?.current && (
                <span style={{ ...muted, fontSize: 12 }}>
                  {consent.current.createdAt} · 기록 {consent.current.userId || "관리자"} · {consent.current.channel === "signup" ? "가입 화면" : consent.current.channel === "admin" ? "관리자 대리(서면)" : "회사 계정 화면"} · 문안 {consent.current.version}
                </span>
              )}
              {consent?.stale && <span style={{ fontSize: 12, color: "#b91c1c" }}>문안이 바뀌었습니다. 새 문안으로 다시 동의해 주세요.</span>}
            </div>
            <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
              {!consent?.agreed || consent?.stale ? (
                <button style={primary} disabled={busy} onClick={() => act(true)} data-testid="consent-agree">정보공개에 동의</button>
              ) : (
                <button style={btn} disabled={busy} onClick={() => act(false)} data-testid="consent-withdraw">동의 철회</button>
              )}
              <button style={btn} onClick={() => setShowText((v) => !v)} data-testid="consent-text-toggle">{showText ? "문안 접기" : "동의 문안 전문 보기"}</button>
            </div>
            {notice && <div style={{ marginTop: 8, fontSize: 12.5 }} data-testid="consent-notice">{notice}</div>}
            {showText && text && (
              <div style={{ marginTop: 10, padding: "10px 12px", borderLeft: "2px solid var(--audit-border, #e5e7eb)" }} data-testid="consent-text">
                <div style={{ fontWeight: 700 }}>{text.title} <span style={{ ...muted, fontWeight: 500, fontSize: 12 }}>({text.version})</span></div>
                <ol style={{ margin: "6px 0 0", paddingLeft: 20 }}>
                  {(text.lines || []).map((ln, i) => <li key={i} style={{ marginTop: 3 }}>{ln}</li>)}
                </ol>
              </div>
            )}
            {Array.isArray(consent?.history) && consent.history.length > 1 && (
              <details style={{ marginTop: 10 }}>
                <summary style={{ cursor: "pointer", fontSize: 12.5, ...muted }}>이력 {consent.history.length}건</summary>
                <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse", marginTop: 6 }}>
                  <tbody>
                    {consent.history.map((h) => (
                      <tr key={h.id} style={{ borderTop: "1px solid var(--audit-border, #eee)" }}>
                        <td style={{ padding: "4px 6px", whiteSpace: "nowrap" }}>{h.createdAt}</td>
                        <td style={{ padding: "4px 6px", fontWeight: 700 }}>{h.agreed ? "동의" : "철회"}</td>
                        <td style={{ padding: "4px 6px" }}>{h.userId || "—"}</td>
                        <td style={{ padding: "4px 6px", ...muted }}>{h.channel}{h.evidence ? ` · ${h.evidence}` : ""}</td>
                        <td style={{ padding: "4px 6px", ...muted }}>{h.version}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            )}
          </div>

          <RequestForm me={me} token={token} opts={opts} onDone={load} />
        </>
      )}
    </div>
  );
}

function RequestForm({ me, token, opts, onDone }) {
  const p = me.profile || {};
  const [f, setF] = useState({ corp_name: p.corpName || "", corp_name_en: p.corpNameEn || "", ceo_name: p.ceoName || "", corp_addr: p.corpAddr || "", contact_name: p.contactName || me.user_name || "", contact_phone: p.contactPhone || "", contact_email: p.contactEmail || "", role: p.role || "", customs_office: p.customsOffice || "", nts_status: p.ntsStatus || "", biz_type: p.bizType || "", biz_item: p.bizItem || "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [looking, setLooking] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  // 66차: 사업자번호(가입 계정의 것)로 관세청·국세청 정보 불러오기 — 채워진 값은 확인 뒤 요청에 실린다
  const lookup = async () => {
    setLooking(true); setMsg(null);
    try {
      const r = await lookupBusiness(token, me.business_no, opts);
      if (!r.found) { setMsg(r.message || "조회 결과가 없습니다. 직접 입력해 주세요."); return; }
      setF((s) => fillFromLookup(s, r.info));
      setMsg(`불러왔습니다 — ${r.info.corp_name}${r.info.nts_status ? ` · 국세청 ${r.info.nts_status}` : ""}. 확인한 뒤 요청하세요.`);
    } catch (err) { setMsg(`조회 실패 (${err.message})`); } finally { setLooking(false); }
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!f.corp_name.trim()) { setMsg("회사명을 입력해 주세요."); return; }
    setBusy(true); setMsg(null);
    try {
      await postCompanyRequest(token, { ...f, role: f.role || null }, opts);
      setMsg(p.status === "verified" ? "연락처가 갱신되었습니다." : "확인 요청이 접수되었습니다. 관리자가 사업자등록 정보를 확인한 뒤 상태가 바뀝니다.");
      onDone && onDone();
    } catch (err) { setMsg(`요청 실패 (${err.message})`); } finally { setBusy(false); }
  };
  return (
    <form style={card} onSubmit={submit} data-testid="company-request">
      <h2 style={h2}>{p.status === "verified" ? "회사 연락처" : "회사 계정 확인 요청"}</h2>
      <div style={{ ...muted, fontSize: 12.5, marginBottom: 8 }}>
        관리자가 사업자등록 정보를 확인하고 자사 신고 자료의 키(관세사 사무소·화주 사업자번호)를 연결합니다. 확인 뒤 자사 상세 보기가 열립니다.
      </div>
      <div style={{ marginBottom: 8, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <button type="button" style={btn} onClick={lookup} disabled={looking} data-testid="request-lookup">{looking ? "조회 중…" : `사업자번호 ${formatBusinessNo(me.business_no)} 로 회사 정보 불러오기`}</button>
        {(f.nts_status || f.customs_office) && <span style={{ ...muted, fontSize: 12 }} data-testid="request-biz-meta">{[f.nts_status && `국세청 ${f.nts_status}`, f.customs_office, f.biz_type && `${f.biz_type} · ${f.biz_item}`].filter(Boolean).join(" · ")}</span>}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 12px" }}>
        <div><label style={label}>회사명 *</label><input style={input} value={f.corp_name} onChange={set("corp_name")} placeholder="(주)회사명" /></div>
        <div><label style={label}>대표자</label><input style={input} value={f.ceo_name} onChange={set("ceo_name")} /></div>
        <div style={{ gridColumn: "1 / -1" }}><label style={label}>주소</label><input style={input} value={f.corp_addr} onChange={set("corp_addr")} data-testid="request-corp-addr" /></div>
        <div><label style={label}>담당자</label><input style={input} value={f.contact_name} onChange={set("contact_name")} /></div>
        <div><label style={label}>담당자 전화</label><input style={input} value={f.contact_phone} onChange={set("contact_phone")} /></div>
        <div><label style={label}>담당자 이메일</label><input style={input} value={f.contact_email} onChange={set("contact_email")} /></div>
        <div>
          <label style={label}>역할</label>
          <select style={input} value={f.role} onChange={set("role")}>
            <option value="">선택</option>
            <option value="broker">관세사(신고인)</option>
            <option value="shipper">화주(수입·수출자)</option>
            <option value="both">관세사·화주</option>
          </select>
        </div>
      </div>
      <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "center" }}>
        <button type="submit" style={primary} disabled={busy}>{p.status === "verified" ? "연락처 갱신" : "확인 요청"}</button>
        {msg && <span style={{ fontSize: 12.5 }} data-testid="request-msg">{msg}</span>}
      </div>
    </form>
  );
}
